/**
 * 방 개설 이력(S28) — "내가 연 회의실"(살아 있는 방)과 달리 **종료 여부와
 * 무관하게 전부** 보여 준다. `MyRoomsPanel` 과 구조를 같이 쓰되(로드
 * 타이밍·로딩/에러 처리·스타일), 개설 이력은 평소에 볼 일이 적으므로
 * **기본 접힘**이다 — 펼쳐야 로드한다.
 */

import { useCallback, useState } from 'react'
import { Card, CardContent } from '@cupia/ui/card'
import { Button } from '@cupia/ui/button'
import { Input } from '@cupia/ui/input'
import { Spinner } from '@cupia/ui/spinner'
import { ChevronRight } from 'lucide-react'
import {
  adminListCreatedRooms,
  listCreatedRooms,
  type RoomCreationRow,
} from '../lib/api'
import { directionLabel } from '../lib/room-info'
import { formatStamp } from '../lib/admin'

/** 표에 쓰는 짧은 방 ID(가독성) — server/roomadmin.py `short_code` 와 같은 길이. */
const SHORT_CODE_CHARS = 8

function shortCode(room: string): string {
  return room.length <= SHORT_CODE_CHARS ? room : room.slice(0, SHORT_CODE_CHARS)
}

const PAGE_LIMIT = 20

export interface RoomHistoryPanelProps {
  /** 관리자 변형 — 전체 개설 이력 + 소유자 열/필터(admin-only). */
  isAdmin?: boolean
}

export function RoomHistoryPanel({ isAdmin = false }: RoomHistoryPanelProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [items, setItems] = useState<RoomCreationRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState(false)
  const [owner, setOwner] = useState('')

  const load = useCallback(
    async (nextOffset: number, replace: boolean) => {
      setBusy(true)
      try {
        const res = isAdmin
          ? await adminListCreatedRooms(PAGE_LIMIT, nextOffset, owner)
          : await listCreatedRooms(PAGE_LIMIT, nextOffset)
        const list = Array.isArray(res?.items) ? res.items : []
        setItems((prev) => (replace ? list : [...prev, ...list]))
        setTotal(typeof res?.total === 'number' ? res.total : list.length)
        setOffset(nextOffset)
        setFailed(false)
      } catch {
        // 실패해도 페이지 나머지가 죽으면 안 된다 — 조용히 접는다.
        setFailed(true)
      } finally {
        setLoaded(true)
        setBusy(false)
      }
    },
    [isAdmin, owner],
  )

  const toggle = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    if (next && !loaded) void load(0, true)
  }, [expanded, loaded, load])

  const reload = useCallback(() => {
    void load(0, true)
  }, [load])

  if (failed && !items.length) return <></>

  return (
    <Card className="md:col-span-2" data-testid="room-history">
      <CardContent className="flex flex-col gap-3 p-4">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-2 text-start"
          data-testid="room-history-toggle"
        >
          <ChevronRight
            className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <span className="font-medium">{isAdmin ? '전체 개설 이력' : '개설 이력'}</span>
        </button>

        {expanded && (
          <div className="flex flex-col gap-3">
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Input
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="소유자로 필터"
                  className="max-w-xs"
                />
                <Button variant="outline" size="sm" onClick={reload}>
                  적용
                </Button>
              </div>
            )}

            {!loaded && busy && (
              <div className="flex items-center gap-2 p-2 text-[var(--color-muted-foreground)]">
                <Spinner className="h-4 w-4" />
                불러오는 중…
              </div>
            )}

            {loaded && !failed && items.length === 0 && (
              <p className="p-2 text-(length:--text-sm-size) text-[var(--color-muted-foreground)]">
                개설 이력이 없습니다.
              </p>
            )}

            {items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-(length:--text-sm-size)">
                  <thead className="border-b border-[var(--color-border)] text-[var(--color-muted-foreground)]">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">이름</th>
                      <th className="px-3 py-2 text-start font-medium">주제</th>
                      {isAdmin && <th className="px-3 py-2 text-start font-medium">소유자</th>}
                      <th className="px-3 py-2 text-start font-medium">방향</th>
                      <th className="px-3 py-2 text-start font-medium">개설일</th>
                      <th className="px-3 py-2 text-start font-medium">종료일</th>
                      <th className="px-3 py-2 text-start font-medium">기록</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.room}
                        data-testid={`room-history-row-${r.room}`}
                        className="border-b border-[var(--color-border)] last:border-0"
                      >
                        <td className="px-3 py-2">{r.name || `회의실 ${shortCode(r.room)}`}</td>
                        <td className="px-3 py-2">{r.topic || '—'}</td>
                        {isAdmin && <td className="px-3 py-2">{r.owner || '—'}</td>}
                        <td className="px-3 py-2">
                          {directionLabel(r.src, r.dst, { bidirectional: r.bidirectional })}
                        </td>
                        <td className="px-3 py-2">{formatStamp(r.created_at)}</td>
                        <td className="px-3 py-2">
                          {r.closed_at === 0 ? '진행 중' : formatStamp(r.closed_at)}
                        </td>
                        <td className="px-3 py-2">
                          {r.dump_audio && (
                            <span className="rounded-[var(--radius-xs)] bg-[var(--color-muted)] px-1.5 py-0.5 text-(length:--text-xs-size)">
                              녹음중
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {items.length < total && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void load(offset + PAGE_LIMIT, false)}
              >
                더 보기
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
